import { type OpenedContract, toNano } from '@ton/core'
import type { BoxV0 } from '@voteboxio/core'
import { useState, useEffect, useCallback } from 'react'

const ESTIMATION_TOLERANCE = {
  PERCENTAGE: 0.5, // 50%
  ABSOLUTE: 1.0, // 1 TON
}

export const useVoteEstimation = (
  openedContract: OpenedContract<BoxV0.BoxV0> | undefined,
  optionIndex: number,
) => {
  const [estimatedVotes, setEstimatedVotes] = useState<bigint | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track last amount used for estimation to implement tolerance
  const [lastAmount, setLastAmount] = useState<string>('')

  const shouldUpdateEstimate = useCallback(
    (newAmount: string, oldAmount: string): boolean => {
      if (!oldAmount || !newAmount) {
        return true
      }

      const newValue = parseFloat(newAmount)
      const oldValue = parseFloat(oldAmount)

      if (newValue <= 0 || oldValue <= 0) {
        return true
      }

      // Check if change is more than 1 TON
      const absoluteChange = Math.abs(newValue - oldValue)
      if (absoluteChange >= ESTIMATION_TOLERANCE.ABSOLUTE) {
        return true
      }

      // Check if change is more than 50%
      const percentageChange = Math.abs((newValue - oldValue) / oldValue)
      if (percentageChange >= ESTIMATION_TOLERANCE.PERCENTAGE) {
        return true
      }

      return false
    },
    [],
  )

  const getVoteEstimation = useCallback(
    async (amount: string): Promise<void> => {
      if (!openedContract || !amount || parseFloat(amount) <= 0) {
        setEstimatedVotes(null)
        setError(null)
        return
      }

      // Check if we should skip this update based on tolerance
      if (!shouldUpdateEstimate(amount, lastAmount)) {
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        const tonAmount = toNano(amount)

        // Call the contract getter method
        const estimation = await openedContract.getEstimateVotesForTons(
          tonAmount,
          BigInt(optionIndex),
        )

        setEstimatedVotes(estimation)
        setLastAmount(amount)
      } catch (error) {
        console.error('Error getting vote estimation:', error)
        setError('Failed to get vote estimation')
        setEstimatedVotes(null)
      } finally {
        setIsLoading(false)
      }
    },
    [openedContract, shouldUpdateEstimate, lastAmount, optionIndex],
  )

  // Reset when contract changes
  useEffect(() => {
    setEstimatedVotes(null)
    setError(null)
    setLastAmount('')
  }, [openedContract])

  return {
    estimatedVotes,
    isLoading,
    error,
    getVoteEstimation,
  }
}
